import _ from 'lodash';
import { observable, computed, action, transaction } from 'mobx';

import { isSEOBot, removeTypename } from 'shared/helpers/utils';
import { segmentIdentifyAndGroup, buildTraitsFromUser } from 'shared/helpers/tools';
import { identifyLDUser } from 'shared/observability/ld-identify';
import { isAdminAccessible, isChainAdmin, isConsumerUser, isSuperAdmin, isDispensaryUser } from 'shared/helpers/users';

import updateAdminUser from 'shared/graphql/user/mutations/update-admin-user.gql';
import { validatePasswordResetTokenQuery, validatePasswordTokenQuery } from 'shared/graphql/user/queries';
import AdminPublicEnv from 'src/utils/admin-public-env';
import { redirectToBackofficeHome } from 'src/utils/backoffice-auth';

import resetPasswordV2 from 'shared/graphql/password/mutations/reset-password-v2.gql';
import sendPasswordResetEmailV2 from 'shared/graphql/password/mutations/send-password-reset-email-v2.gql';
import setPasswordV2 from 'shared/graphql/password/mutations/set-passwordV2.gql';
import updatePasswordV2 from 'shared/graphql/password/mutations/update-password-v2.gql';
import logoutMutation from 'shared/graphql/user/mutations/logout.gql';
import loginAdmin from 'shared/graphql/user/mutations/login-admin.gql';
import loginAdminViaToken from 'shared/graphql/user/mutations/login-admin-via-token.gql';
import { EmptyObject, WhenNotVoid } from 'shared/utils/type-utils';
import meAdmin from 'src/state/me-admin.gql';
import {
  GqlLoginAdminMutation,
  GqlLoginAdminMutationVariables,
  GqlLoginAdminViaTokenMutation,
  GqlLoginAdminViaTokenMutationVariables,
  GqlLogoutMutation,
  GqlLogoutMutationVariables,
  GqlMeAdminQuery,
  GqlMeAdminQueryVariables,
  GqlMutationUpdateUserArgs,
  GqlResetPasswordV2Mutation,
  GqlResetPasswordV2MutationVariables,
  GqlSendPasswordResetEmailV2Mutation,
  GqlSendPasswordResetEmailV2MutationVariables,
  GqlSetPasswordV2Mutation,
  GqlSetPasswordV2MutationVariables,
  GqlUpdatePasswordV2Mutation,
  GqlUpdatePasswordV2MutationVariables,
} from 'types/graphql';
import { RootState } from './app';

// Thrown by login() when LoginAdmin succeeds but the immediately-following
// session-establishing meAdmin lookup fails, so no session actually got set up.
// Exported so callers (e.g. the login form) can show a distinct message rather
// than the generic "incorrect email or password" one.
export const SESSION_ESTABLISH_FAILURE_MESSAGE = 'Unable to establish a session after login';

type User = WhenNotVoid<GqlMeAdminQuery['meAdmin']>;
type Profile = WhenNotVoid<User['profile']>;
type LoginResponse =
  | {
      error: any;
      success: boolean;
      /**
       * True when `error` represents a transport/network failure (the request never reached
       * the server, e.g. Apollo's `ApolloError.networkError` — think "Failed to fetch"), as
       * opposed to a genuine server-side rejection (e.g. an expired/invalid token). Callers
       * should show a distinct "connection problem, try again" message in that case rather
       * than implying the credential/token itself was bad.
       */
      isNetworkError?: boolean;
    }
  | {
      success: boolean;
      error?: undefined;
      isNetworkError?: undefined;
    };

export class UserState {
  @observable _user: EmptyObject | User = {};
  @observable chainIds: Array<string | null> = [];
  @observable email: string | null | undefined;
  @observable enterpriseId: string | null | undefined;
  @observable exists = false;
  @observable id: string | null | undefined;
  @observable initialUserFetchFinished = false;
  @observable profile: EmptyObject | Profile = {};

  constructor(private readonly rootStore: RootState) {
    void this.fetchUser();
  }

  fetchUser = async (force = false): Promise<{ networkError?: boolean }> => {
    let user: User | undefined;

    // detect if we're currently doing the transfer token dance
    // we don't want to fetch the current user until it's done
    const transferPath = `${AdminPublicEnv.adminBasePath ?? ''}/transfer`;
    if (!force && window.location.pathname === transferPath) {
      // just don't, we're about to get redirected anyway
      return {};
    }

    if (this.getHasToken()) {
      try {
        const response = await this.rootStore.apolloClient.query<GqlMeAdminQuery, GqlMeAdminQueryVariables>({
          fetchPolicy: 'no-cache',
          query: meAdmin,
        });

        user = removeTypename(response.data.meAdmin);
      } catch (e) {
        if (e.networkError) {
          // If network error, don't reset whole session.
          console.warn('network error');
          return { networkError: true };
        }

        console.warn('Error Fetching User', e);
        this.rootStore.UI.showErnie('Something went wrong. Please try logging in again.', 'danger');
      }
    }

    await this.handleSetupUser(user);
    return {};
  };

  handleSetupUser = async (user?: User): Promise<void> => {
    const shouldBootServices = !isSEOBot() && AdminPublicEnv.appEnv === 'production';

    if (user && !isAdminAccessible(user)) {
      if (AdminPublicEnv.isBackofficeHosted) {
        redirectToBackofficeHome();
        return;
      }

      await this.logout();
      window.location.href = `${AdminPublicEnv.consumerUrl}/login`;
      return;
    }

    segmentIdentifyAndGroup(user);

    if (shouldBootServices && user) {
      try {
        identifyLDUser(user._id, buildTraitsFromUser(user));
      } catch (e) {
        console.error(e);
      }
    }

    if (user) {
      await transaction(async () => {
        this._user = user;
        this.chainIds = user.chainDispensaryIds ?? [];
        this.email = user.emails[0]?.address;
        this.enterpriseId = user.enterpriseId;
        this.exists = true;
        this.id = user._id;
        this.initialUserFetchFinished = true;
        this.profile = user.profile;
      });
    } else {
      transaction(() => {
        this._user = {};
        this.chainIds = [];
        this.email = null;
        this.enterpriseId = '';
        this.exists = false;
        this.id = null;
        this.initialUserFetchFinished = true;
        this.profile = {};
      });
    }
  };

  getHasToken = (): boolean =>
    AdminPublicEnv.isBackofficeHosted || !_.isEmpty(window.localStorage.getItem('access-token'));

  @action updateUser = async ({
    audioNotificationsOnNewArrivalsDisabled,
    audioNotificationsOnNewOrdersDisabled,
    browserNotification,
    desktopNotification,
    email,
    emailNotifications,
    fullName,
    isWeeklyReportEnabled,
    phone,
    roleAtDispensary,
  }: GqlMutationUpdateUserArgs['user']): Promise<void> => {
    this.email = email;
    this.profile = {
      ...this.profile,
      audioNotificationsOnNewArrivalsDisabled,
      audioNotificationsOnNewOrdersDisabled,
      browserNotification,
      desktopNotification,
      emailNotifications,
      fullName,
      isWeeklyReportEnabled,
      phone,
      roleAtDispensary,
    };

    if (this.profile.permissions && this.profile.permissions.dispensaryIds === undefined) {
      const activeDispensary = this.rootStore.UI.dispensary.id;
      if (this.profile.dispensaryId) {
        this.profile.permissions.dispensaryIds = [this.profile.dispensaryId];
      } else if (activeDispensary) {
        this.profile.permissions.dispensaryIds = [activeDispensary];
      }
    }

    // TODO: add type information for mutate
    const response = await this.rootStore.apolloClient.mutate({
      mutation: updateAdminUser,
      variables: {
        input: removeTypename({
          _id: this.id,
          audioNotificationsOnNewArrivalsDisabled,
          audioNotificationsOnNewOrdersDisabled,
          browserNotification,
          desktopNotification,
          email,
          emailNotifications,
          fullName,
          isWeeklyReportEnabled,
          permissions: _.omit(
            this.profile.permissions,
            'tasks',
            'techSupportFeatures',
            'manageAds',
            'managePersonalization'
          ),
          phone,
          roleAtDispensary,
        }),
      },
    });

    return response.data.updateUser;
  };

  logout = async (): Promise<void> => {
    if (AdminPublicEnv.isBackofficeHosted) {
      return;
    }

    const hasToken = this.getHasToken();

    if (hasToken) {
      try {
        await this.rootStore.apolloClient.mutate<GqlLogoutMutation, GqlLogoutMutationVariables>({
          mutation: logoutMutation,
        });
      } catch (error) {
        console.error(error);
      }
    }

    this.handleSetupAuthorizationToken();
    window.localStorage.removeItem('redirectAfterLogin');
    await this.handleSetupUser();
  };

  handleSetupAuthorizationToken = (accessToken?: string | null): void => {
    if (!accessToken) {
      window.localStorage.removeItem('access-token');
    } else {
      window.localStorage.setItem('access-token', accessToken);
    }

    const token = window.localStorage.getItem('access-token');
    this.rootStore.Pusher.resetToken(token);
  };

  @action login = async (email: string, password: string): Promise<LoginResponse> => {
    try {
      const response = await this.rootStore.apolloClient.mutate<GqlLoginAdminMutation, GqlLoginAdminMutationVariables>({
        mutation: loginAdmin,
        variables: {
          email,
          password,
        },
      });

      const accessToken = response.data?.loginAdmin?.accessToken;

      this.handleSetupAuthorizationToken(accessToken);
      const { networkError } = await this.fetchUser();

      if (!this.loggedIn) {
        // LoginAdmin succeeded, but the meAdmin lookup fetchUser() just ran to
        // establish the session failed (e.g. a backend dependency was
        // unavailable) and fetchUser() swallows that into an empty user rather
        // than rethrowing. Don't report success with no session established:
        // the caller treats `success: true` as "go to /redirect", and
        // RedirectUser's app-switch effect treats an unrecognized/empty user as
        // a consumer visitor and bounces them to the consumer site with no
        // explanation. Fail the login instead so the caller keeps the user on
        // /login and shows them an error.
        if (networkError) {
          // fetchUser() stays silent on a network error so a transient blip on
          // page load doesn't nuke an existing session -- but here it means no
          // ernie has been shown yet, so show one before failing the login.
          this.rootStore.UI.showErnie('Something went wrong. Please try logging in again.', 'danger');
        }
        throw new Error(SESSION_ESTABLISH_FAILURE_MESSAGE);
      }

      return { success: true };
    } catch (error) {
      console.error(error);
      return { error, success: false, isNetworkError: Boolean(error?.networkError) };
    }
  };

  @action loginViaToken = async (transferToken: string): Promise<LoginResponse> => {
    try {
      const response = await this.rootStore.apolloClient.mutate<
        GqlLoginAdminViaTokenMutation,
        GqlLoginAdminViaTokenMutationVariables
      >({
        mutation: loginAdminViaToken,
        variables: {
          transferToken,
        },
      });

      const accessToken = response.data?.loginAdminViaToken?.accessToken;

      this.handleSetupAuthorizationToken(accessToken);
      await this.fetchUser(true);

      return { success: true };
    } catch (error) {
      console.error(error);
      // Mirrors fetchUser's e.networkError handling: distinguish a transport failure (request
      // never reached the server, e.g. "Failed to fetch") from a genuine server-side rejection
      // (e.g. an expired/invalid transfer token) so callers can show an accurate message and
      // avoid telling the user their token is bad when it's actually a connectivity blip.
      return { error, success: false, isNetworkError: Boolean(error?.networkError) };
    }
  };

  @computed get user(): EmptyObject | User {
    return this._user;
  }

  @computed get loggedIn(): boolean {
    return !_.isEmpty(this._user);
  }

  @computed get isSuperAdmin(): boolean {
    return isSuperAdmin({ profile: this.profile });
  }

  // this function has a somewhat misleading name
  // it does not check if the user has admin over a chain, but rather if they are an admin in any chain
  @computed get isChainAdmin(): boolean {
    return isChainAdmin({ profile: this.profile });
  }

  @computed get isEnterpriseAdmin(): boolean {
    return this.profile.permissions?.enterpriseAdmin ?? false;
  }

  @computed get isOutsourceCleaner(): boolean {
    return isSuperAdmin({ profile: this.profile }) && !!this.profile.permissions?.menuReview;
  }

  @computed get hasTasksPermissions(): boolean {
    return isSuperAdmin({ profile: this.profile }) && !!this.profile.permissions?.tasks;
  }

  @computed get hasAdsPermissions(): boolean {
    return isSuperAdmin({ profile: this.profile }) && !!this.profile.permissions?.manageAds;
  }

  @computed get hasPersonalizationPermissions(): boolean {
    return isSuperAdmin({ profile: this.profile }) && !!this.profile.permissions?.managePersonalization;
  }

  @computed get hasAnalyticsPermissions(): boolean {
    return (
      isSuperAdmin({ profile: this.profile }) ||
      (isDispensaryUser({ profile: this.profile }) && !!this.profile.permissions?.analytics)
    );
  }
  @computed get hasBillingPermissions(): boolean {
    return (
      isSuperAdmin({ profile: this.profile }) ||
      (isDispensaryUser({ profile: this.profile }) && !!this.profile.permissions?.billing)
    );
  }
  @computed get hasCustomersPermissions(): boolean {
    return (
      isSuperAdmin({ profile: this.profile }) ||
      (isDispensaryUser({ profile: this.profile }) && !!this.profile.permissions?.customers)
    );
  }
  @computed get hasMenuPermissions(): boolean {
    return (
      isSuperAdmin({ profile: this.profile }) ||
      (isDispensaryUser({ profile: this.profile }) && !!this.profile.permissions?.menu)
    );
  }
  @computed get hasPromotePermissions(): boolean {
    return (
      isSuperAdmin({ profile: this.profile }) ||
      (isDispensaryUser({ profile: this.profile }) && !!this.profile.permissions?.promote)
    );
  }
  @computed get hasSettingsPermissions(): boolean {
    return (
      isSuperAdmin({ profile: this.profile }) ||
      (isDispensaryUser({ profile: this.profile }) && !!this.profile.permissions?.settings)
    );
  }
  @computed get hasUsersPermissions(): boolean {
    return (
      isSuperAdmin({ profile: this.profile }) ||
      (isDispensaryUser({ profile: this.profile }) && !!this.profile.permissions?.users)
    );
  }

  @computed get hasOrdersPermissions(): boolean {
    return (
      isSuperAdmin({ profile: this.profile }) ||
      (isDispensaryUser({ profile: this.profile }) && !!this.profile.permissions?.orders)
    );
  }

  @computed get hasMessagingPermissions(): boolean {
    return (
      isSuperAdmin({ profile: this.profile }) ||
      (isDispensaryUser({ profile: this.profile }) && !!this.profile.permissions?.messaging)
    );
  }

  @action hasAccessToDispensary(dispensaryId: string): boolean {
    return (
      // super admins like dutchie staff
      isSuperAdmin({ profile: this.profile }) ||
      // is a dispensary admin of some sort
      (isDispensaryUser({ profile: this.profile }) &&
        // non-chain single dispo admin
        (this.profile.dispensaryId === dispensaryId ||
          // full chain admin — but only for dispensaries within their own chain.
          // `allLocations` means "all locations in my chain", so scope it to the chain's
          // dispensaries (chainIds is sourced from meAdmin.chainDispensaryIds).
          (!!this.profile.permissions?.allLocations && _.includes(this.chainIds, dispensaryId)) ||
          // chain admin with permissions to this specific dispensary
          _.includes(this.profile.permissions?.dispensaryIds, dispensaryId)))
    );
  }

  @computed get isSuperOutsourceCleaner(): boolean {
    return isSuperAdmin({ profile: this.profile }) && !!this.profile.permissions?.superMenuReview;
  }

  @computed get isDispensaryUser(): boolean {
    return isDispensaryUser({ profile: this.profile });
  }

  @computed get isConsumerUser(): boolean {
    return isConsumerUser({ profile: this.profile });
  }

  @computed get fullName(): string {
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    return this.profile.fullName || `${this.profile.firstName} ${this.profile.lastName}`;
  }

  @action updatePasswordWhenLoggedIn = async (oldPassword: string, newPassword: string): Promise<void> => {
    const response = await this.rootStore.apolloClient.mutate<
      GqlUpdatePasswordV2Mutation,
      GqlUpdatePasswordV2MutationVariables
    >({
      mutation: updatePasswordV2,
      variables: {
        oldPassword,
        newPassword,
      },
    });

    this.handleSetupAuthorizationToken(response.data?.updatePasswordV2?.accessToken);
  };

  @action sendPasswordResetEmail = async (
    email: string
  ): Promise<{
    success: boolean;
    data?: GqlSendPasswordResetEmailV2Mutation | null;
    err?: any;
  }> => {
    try {
      const response = await this.rootStore.apolloClient.mutate<
        GqlSendPasswordResetEmailV2Mutation,
        GqlSendPasswordResetEmailV2MutationVariables
      >({
        mutation: sendPasswordResetEmailV2,
        variables: { email },
      });
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, err };
    }
  };

  @action validateResetToken = async (token: string): Promise<void> => {
    // TODO: add type information for query
    const response = await this.rootStore.apolloClient.query({
      query: validatePasswordResetTokenQuery,
      variables: {
        token,
      },
    });

    return response.data.validatePasswordResetTokenQuery.isValid;
  };

  @action validatePasswordToken = async (token: string): Promise<boolean> => {
    // TODO: add type information for query
    const response = await this.rootStore.apolloClient.query({
      query: validatePasswordTokenQuery,
      variables: {
        token,
      },
    });

    return response.data.validatePasswordTokenQuery.isValid;
  };

  @action setPassword = async (
    token: string,
    password: string
  ): Promise<{
    success: boolean;
    data?: GqlSetPasswordV2Mutation | null;
    err?: any;
  }> => {
    try {
      const result = await this.rootStore.apolloClient.mutate<
        GqlSetPasswordV2Mutation,
        GqlSetPasswordV2MutationVariables
      >({
        mutation: setPasswordV2,
        variables: { input: { token, password } },
      });
      return { success: true, data: result.data };
    } catch (err) {
      return { success: false, err };
    }
  };

  @action resetPassword = async (
    token: string,
    password: string
  ): Promise<{
    success: boolean;
    data?: GqlResetPasswordV2Mutation | null;
    err?: any;
  }> => {
    try {
      const response = await this.rootStore.apolloClient.mutate<
        GqlResetPasswordV2Mutation,
        GqlResetPasswordV2MutationVariables
      >({
        mutation: resetPasswordV2,
        variables: { input: { token, password } },
      });
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, err };
    }
  };
}
